import { useState } from "react";
import { useTheme } from "../../context/ThemeContext";
import {
  submitDepositRequest,
  validateDepositAmount,
  formatDepositData,
  getPaymentMethodConfig
} from "../../utils/depositApi";
import api from "../../api/Api";
import DepositSuccessModal from "../../components/DepositSuccessModal";

const QUICK_AMOUNTS = [500, 1000, 2000, 5000, 10000, 25000];

// Company bank details for bank transfer
const COMPANY_BANK_DETAILS = {
  bankName: "State Bank of India",
  accountName: "FileBharti Private Limited",
  accountNumber: "32145678901",
  ifscCode: "SBIN0001234",
  branchName: "Main Branch, New Delhi",
  accountType: "Current Account"
};

// UPI Details
const COMPANY_UPI_DETAILS = {
  upiId: "filebharti@paytm",
  merchantName: "FileBharti Pvt Ltd",
  qrCodeUrl: "/api/upi-qr-code" // This should be replaced with actual QR code image URL
};

export default function AddFundsModal({ isOpen, onClose, walletBalance }) {
  const { isDarkMode } = useTheme();
  const [amount, setAmount] = useState("");
  const [selectedQuick, setSelectedQuick] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("bank");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [transactionRef, setTransactionRef] = useState("");
  const [loading, setLoading] = useState(false);
  const [copiedField, setCopiedField] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successAmount, setSuccessAmount] = useState(null);

  if (!isOpen) return null;

  const handleQuickSelect = (val) => {
    setSelectedQuick(val);
    setAmount(String(val));
    setError("");
  };

  const handleManualInput = (e) => {
    setSelectedQuick(null);
    setAmount(e.target.value);
    setError("");
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        setError("File size should be less than 5MB");
        return;
      }
      setReceipt(file);
      setError("");
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(""), 2000);
  };

  const handleSubmit = async () => {

    const validation = validateDepositAmount(amount, paymentMethod);
    if (!validation.valid) {
      setError(validation.error);
      return;
    }

    const methodConfig = getPaymentMethodConfig(paymentMethod);

    if (methodConfig.requiresReceipt && !receipt) {
      setError("Please upload payment receipt");
      return;
    }

    if (methodConfig.requiresRef && !transactionRef.trim()) {
      setError("Please enter transaction reference number");
      return;
    }

    setLoading(true);
    setError("");

    try {

      const formData = new FormData();
      formData.append("amount", amount);
      formData.append("payment_method", paymentMethod);
      formData.append("transaction_ref", transactionRef);

      if (receipt) {
        formData.append("receipt", receipt);
      }

      // ✅ USING YOUR AXIOS INSTANCE (with token auto attach)
      const { data } = await api.post(
        "start-request",
        formData
      );

      if (data?.status || data?.success) {

        setSuccess(true);

        // Show success modal with the deposited amount
        setSuccessAmount(parseFloat(amount));
        setShowSuccessModal(true);

        // Reset form after showing success
        setAmount("");
        setTransactionRef("");
        setReceipt(null);
        setSelectedQuick(null);

        // Close the add funds modal after a delay
        setTimeout(() => {
          onClose();
          setSuccess(false);
        }, 6000); // Close main modal 1 second after success modal closes

      } else {
        setError(data?.message || "Deposit request failed");
      }

    } catch (error) {

      console.error(error);

      setError(
        error?.response?.data?.message ||
        "Server error. Please try again."
      );

    } finally {
      setLoading(false);
    }
  };

  const dark = isDarkMode;

  const styles = {
    overlay: {
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.6)",
      backdropFilter: "blur(4px)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1000,
      padding: "1rem",
    },
    modal: {
      width: "100%",
      maxWidth: 520,
      maxHeight: "90vh",
      overflowY: "auto",
      background: dark
        ? "linear-gradient(135deg, #1a1a1a 0%, #2d1b1b 100%)"
        : "linear-gradient(135deg, #ffffff 0%, #f8f8f8 100%)",
      borderRadius: 20,
      border: `1px solid ${dark ? "#333" : "#e5e5e5"}`,
      boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
    },
    header: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "1.5rem 1.5rem 1rem",
      borderBottom: `1px solid ${dark ? "#333" : "#f0f0f0"}`,
      background: dark ? "rgba(128,0,32,0.1)" : "rgba(128,0,32,0.05)",
    },
    headerLeft: {
      display: "flex",
      alignItems: "center",
      gap: 12
    },
    iconBox: {
      width: 40,
      height: 40,
      borderRadius: 10,
      background: "linear-gradient(135deg, #800020 0%, #a01030 100%)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 20,
      boxShadow: "0 4px 12px rgba(128,0,32,0.3)",
    },
    title: {
      fontSize: 18,
      fontWeight: 600,
      color: dark ? "#f0f0f0" : "#111",
      letterSpacing: "-0.01em",
    },
    closeBtn: {
      background: dark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)",
      border: "none",
      cursor: "pointer",
      fontSize: 20,
      color: dark ? "#888" : "#666",
      padding: 8,
      borderRadius: 8,
      lineHeight: 1,
      transition: "all 0.2s",
      "&:hover": {
        background: dark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.1)",
      }
    },
    balanceBox: {
      margin: "1.5rem",
      background: dark
        ? "linear-gradient(135deg, #252525 0%, #1a1a1a 100%)"
        : "linear-gradient(135deg, #f8f8f8 0%, #ffffff 100%)",
      borderRadius: 12,
      padding: "1rem 1.25rem",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      border: `1px solid ${dark ? "#333" : "#e5e5e5"}`,
      boxShadow: dark
        ? "inset 0 2px 4px rgba(0,0,0,0.3)"
        : "inset 0 2px 4px rgba(0,0,0,0.05)",
    },
    balanceLabel: {
      fontSize: 12,
      color: "#888",
      margin: "0 0 4px",
      textTransform: "uppercase",
      letterSpacing: "0.05em",
    },
    balanceAmount: {
      fontSize: 24,
      fontWeight: 600,
      color: dark ? "#f0f0f0" : "#111",
      margin: 0,
      letterSpacing: "-0.02em",
    },
    paymentTabs: {
      display: "flex",
      gap: 8,
      padding: "0 1.5rem",
      marginBottom: "1.5rem",
    },
    paymentTab: (active) => ({
      flex: 1,
      padding: "12px 16px",
      borderRadius: 10,
      border: active
        ? "2px solid #800020"
        : `1px solid ${dark ? "#333" : "#e5e5e5"}`,
      background: active
        ? (dark ? "rgba(128,0,32,0.15)" : "rgba(128,0,32,0.08)")
        : (dark ? "rgba(255,255,255,0.03)" : "#ffffff"),
      cursor: "pointer",
      transition: "all 0.2s",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 4,
    }),
    tabIcon: {
      fontSize: 24,
    },
    tabLabel: (active) => ({
      fontSize: 13,
      fontWeight: active ? 600 : 500,
      color: active
        ? "#800020"
        : (dark ? "#aaa" : "#666"),
    }),
    tabBadge: {
      fontSize: 10,
      padding: "2px 6px",
      borderRadius: 4,
      background: "#fbbf24",
      color: "#000",
      fontWeight: 600,
    },
    sectionLabel: {
      fontSize: 13,
      color: dark ? "#aaa" : "#666",
      margin: "0 0 8px",
      fontWeight: 500,
      textTransform: "uppercase",
      letterSpacing: "0.05em",
    },
    quickGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(3, 1fr)",
      gap: 8,
    },
    quickBtn: (active) => ({
      padding: "10px 8px",
      cursor: "pointer",
      borderRadius: 10,
      fontSize: 14,
      fontWeight: 600,
      border: active
        ? "2px solid #800020"
        : `1px solid ${dark ? "#333" : "#d1d5db"}`,
      background: active
        ? (dark ? "rgba(128,0,32,0.15)" : "rgba(128,0,32,0.08)")
        : (dark ? "rgba(255,255,255,0.03)" : "#fff"),
      color: active
        ? "#800020"
        : (dark ? "#ccc" : "#333"),
      transition: "all 0.2s",
    }),
    input: {
      width: "100%",
      boxSizing: "border-box",
      padding: "12px 14px 12px 32px",
      border: `1px solid ${dark ? "#444" : "#d1d5db"}`,
      borderRadius: 10,
      background: dark ? "rgba(0,0,0,0.3)" : "#fff",
      color: dark ? "#f0f0f0" : "#111",
      fontSize: 16,
      fontWeight: 500,
      outline: "none",
      transition: "border 0.2s",
    },
    bankDetailsBox: {
      background: dark
        ? "rgba(255,255,255,0.03)"
        : "rgba(0,0,0,0.03)",
      borderRadius: 12,
      padding: "1rem",
      border: `1px solid ${dark ? "#333" : "#e5e5e5"}`,
    },
    detailRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "8px 0",
      borderBottom: `1px solid ${dark ? "#2a2a2a" : "#f0f0f0"}`,
    },
    detailLabel: {
      fontSize: 12,
      color: dark ? "#888" : "#666",
      fontWeight: 500,
    },
    detailValue: {
      fontSize: 13,
      color: dark ? "#f0f0f0" : "#111",
      fontWeight: 600,
      display: "flex",
      alignItems: "center",
      gap: 8,
    },
    copyBtn: {
      padding: "4px 8px",
      fontSize: 11,
      background: dark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)",
      border: "none",
      borderRadius: 6,
      cursor: "pointer",
      color: dark ? "#aaa" : "#666",
      transition: "all 0.2s",
    },
    uploadBox: {
      border: `2px dashed ${dark ? "#444" : "#d1d5db"}`,
      borderRadius: 12,
      padding: "2rem",
      textAlign: "center",
      cursor: "pointer",
      background: dark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.01)",
      transition: "all 0.2s",
    },
    qrBox: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 12,
      padding: "1rem",
      background: "#ffffff",
      borderRadius: 12,
      border: "1px solid #e5e5e5",
    },
    qrCode: {
      width: 180,
      height: 180,
      background: "#f0f0f0",
      borderRadius: 8,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 14,
      color: "#666",
    },
    upiId: {
      fontSize: 16,
      fontWeight: 600,
      color: "#111",
      padding: "8px 16px",
      background: "#f8f8f8",
      borderRadius: 8,
      display: "flex",
      alignItems: "center",
      gap: 8,
    },
    submitBtn: {
      width: "100%",
      padding: "14px",
      borderRadius: 12,
      border: "none",
      background: loading || success
        ? (success ? "#16a34a" : "#666")
        : "linear-gradient(135deg, #800020 0%, #a01030 100%)",
      color: "#fff",
      cursor: loading ? "not-allowed" : "pointer",
      fontSize: 15,
      fontWeight: 600,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
      transition: "all 0.3s",
      boxShadow: loading || success
        ? "none"
        : "0 4px 12px rgba(128,0,32,0.3)",
    },
    error: {
      fontSize: 12,
      color: "#ef4444",
      margin: "8px 0 0",
      display: "flex",
      alignItems: "center",
      gap: 4,
    },
    successBox: {
      background: "#16a34a",
      color: "#fff",
      padding: "12px",
      borderRadius: 10,
      fontSize: 14,
      textAlign: "center",
      fontWeight: 500,
    },
    warningBox: {
      background: dark ? "rgba(251,191,36,0.1)" : "rgba(251,191,36,0.08)",
      border: `1px solid ${dark ? "#854d0e" : "#fbbf24"}`,
      borderRadius: 10,
      padding: "12px",
      fontSize: 12,
      color: dark ? "#fbbf24" : "#854d0e",
      display: "flex",
      alignItems: "flex-start",
      gap: 8,
      marginTop: "1rem",
    },
    comingSoon: {
      textAlign: "center",
      padding: "3rem 2rem",
      color: dark ? "#888" : "#666",
    },
  };

  const renderBankTransfer = () => (
    <div style={{ padding: "0 1.5rem 1.5rem" }}>
      <p style={styles.sectionLabel}>Company Bank Details</p>
      <div style={styles.bankDetailsBox}>
        {Object.entries({
          "Bank Name": COMPANY_BANK_DETAILS.bankName,
          "Account Name": COMPANY_BANK_DETAILS.accountName,
          "Account Number": COMPANY_BANK_DETAILS.accountNumber,
          "IFSC Code": COMPANY_BANK_DETAILS.ifscCode,
          "Branch": COMPANY_BANK_DETAILS.branchName,
          "Account Type": COMPANY_BANK_DETAILS.accountType,
        }).map(([label, value]) => (
          <div key={label} style={styles.detailRow}>
            <span style={styles.detailLabel}>{label}</span>
            <span style={styles.detailValue}>
              {value}
              {(label === "Account Number" || label === "IFSC Code") && (
                <button
                  style={styles.copyBtn}
                  onClick={() => copyToClipboard(value, label)}
                >
                  {copiedField === label ? "✓ Copied" : "Copy"}
                </button>
              )}
            </span>
          </div>
        ))}
      </div>

      <div style={{ marginTop: "1.5rem" }}>
        <p style={styles.sectionLabel}>Transaction Reference Number</p>
        <input
          type="text"
          placeholder="Enter UTR/Transaction ID"
          value={transactionRef}
          onChange={(e) => setTransactionRef(e.target.value)}
          style={{ ...styles.input, paddingLeft: 14 }}
        />
      </div>

      <div style={{ marginTop: "1.5rem" }}>
        <p style={styles.sectionLabel}>Upload Payment Receipt</p>
        <label style={styles.uploadBox}>
          <input
            type="file"
            accept="image/*,.pdf"
            onChange={handleFileUpload}
            style={{ display: "none" }}
          />
          {receipt ? (
            <div>
              <span style={{ fontSize: 28 }}>✅</span>
              <p style={{ margin: "8px 0 0", fontSize: 13, color: dark ? "#aaa" : "#666" }}>
                {receipt.name}
              </p>
              <p style={{ margin: "4px 0 0", fontSize: 11, color: "#888" }}>
                Click to change file
              </p>
            </div>
          ) : (
            <div>
              <span style={{ fontSize: 32 }}>📎</span>
              <p style={{ margin: "8px 0 0", fontSize: 14, color: dark ? "#aaa" : "#666" }}>
                Click to upload receipt
              </p>
              <p style={{ margin: "4px 0 0", fontSize: 11, color: "#888" }}>
                JPG, PNG, PDF (Max 5MB)
              </p>
            </div>
          )}
        </label>
      </div>

      <div style={styles.warningBox}>
        <span>⚠️</span>
        <div>
          <strong>Important:</strong> After making the bank transfer, please upload the receipt.
          Your funds will be credited within 2-4 hours after admin approval.
        </div>
      </div>
    </div>
  );

  const renderUPIPayment = () => (
    <div style={{ padding: "0 1.5rem 1.5rem" }}>
      <p style={styles.sectionLabel}>Scan QR Code or Use UPI ID</p>

      <div style={styles.qrBox}>
        <div style={styles.qrCode}>
          {/* Replace with actual QR code image */}
          <span>QR Code</span>
        </div>
        <div style={styles.upiId}>
          {COMPANY_UPI_DETAILS.upiId}
          <button
            style={styles.copyBtn}
            onClick={() => copyToClipboard(COMPANY_UPI_DETAILS.upiId, "upi")}
          >
            {copiedField === "upi" ? "✓ Copied" : "Copy"}
          </button>
        </div>
        <p style={{ fontSize: 12, color: "#666", margin: 0 }}>
          {COMPANY_UPI_DETAILS.merchantName}
        </p>
      </div>

      <div style={{ marginTop: "1.5rem" }}>
        <p style={styles.sectionLabel}>UPI Transaction ID</p>
        <input
          type="text"
          placeholder="Enter 12-digit UPI transaction ID"
          value={transactionRef}
          onChange={(e) => setTransactionRef(e.target.value)}
          style={{ ...styles.input, paddingLeft: 14 }}
        />
      </div>

      <div style={{ marginTop: "1.5rem" }}>
        <p style={styles.sectionLabel}>Upload Payment Screenshot</p>
        <label style={styles.uploadBox}>
          <input
            type="file"
            accept="image/*"
            onChange={handleFileUpload}
            style={{ display: "none" }}
          />
          {receipt ? (
            <div>
              <span style={{ fontSize: 28 }}>✅</span>
              <p style={{ margin: "8px 0 0", fontSize: 13, color: dark ? "#aaa" : "#666" }}>
                {receipt.name}
              </p>
              <p style={{ margin: "4px 0 0", fontSize: 11, color: "#888" }}>
                Click to change file
              </p>
            </div>
          ) : (
            <div>
              <span style={{ fontSize: 32 }}>📱</span>
              <p style={{ margin: "8px 0 0", fontSize: 14, color: dark ? "#aaa" : "#666" }}>
                Upload UPI payment screenshot
              </p>
              <p style={{ margin: "4px 0 0", fontSize: 11, color: "#888" }}>
                JPG, PNG (Max 5MB)
              </p>
            </div>
          )}
        </label>
      </div>

      <div style={styles.warningBox}>
        <span>⚠️</span>
        <div>
          <strong>Note:</strong> After completing UPI payment, upload the screenshot.
          Funds will be credited within 30 minutes after verification.
        </div>
      </div>
    </div>
  );

  const renderOnlinePayment = () => (
    <div style={styles.comingSoon}>
      <span style={{ fontSize: 48, display: "block", marginBottom: 16 }}>🚀</span>
      <h3 style={{ margin: "0 0 8px", fontSize: 18, color: dark ? "#f0f0f0" : "#111" }}>
        Coming Soon!
      </h3>
      <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6 }}>
        Online payment gateway integration is in progress.
        Soon you'll be able to add funds instantly using Credit/Debit cards,
        Net Banking, and other payment methods.
      </p>
      <div style={{
        marginTop: 24,
        padding: "8px 16px",
        background: dark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)",
        borderRadius: 8,
        fontSize: 12,
        color: dark ? "#aaa" : "#666"
      }}>
        Expected launch: Q1 2024
      </div>
    </div>
  );

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <div style={styles.iconBox}>💳</div>
            <span style={styles.title}>Add Funds to Wallet</span>
          </div>
          <button
            style={styles.closeBtn}
            onClick={onClose}
            onMouseEnter={(e) => e.target.style.background = dark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.1)"}
            onMouseLeave={(e) => e.target.style.background = dark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)"}
          >
            ✕
          </button>
        </div>

        {/* Balance */}
        <div style={styles.balanceBox}>
          <div>
            <p style={styles.balanceLabel}>Current Balance</p>
            <p style={styles.balanceAmount}>₹{walletBalance}</p>
          </div>
          <span style={{ fontSize: 32 }}>💰</span>
        </div>

        {/* Amount Selection */}
        <div style={{ padding: "0 1.5rem 1rem" }}>
          <p style={styles.sectionLabel}>Select or Enter Amount</p>
          <div style={styles.quickGrid}>
            {QUICK_AMOUNTS.map((amt) => (
              <button
                key={amt}
                style={styles.quickBtn(selectedQuick === amt)}
                onClick={() => handleQuickSelect(amt)}
                onMouseEnter={(e) => {
                  if (selectedQuick !== amt) {
                    e.target.style.background = dark ? "rgba(128,0,32,0.08)" : "rgba(128,0,32,0.04)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedQuick !== amt) {
                    e.target.style.background = dark ? "rgba(255,255,255,0.03)" : "#fff";
                  }
                }}
              >
                ₹{amt.toLocaleString()}
              </button>
            ))}
          </div>

          <div style={{ position: "relative", marginTop: 12 }}>
            <span style={{
              position: "absolute",
              left: 14,
              top: "50%",
              transform: "translateY(-50%)",
              fontSize: 16,
              color: dark ? "#888" : "#999",
              pointerEvents: "none",
            }}>₹</span>
            <input
              type="number"
              min="100"
              placeholder="Enter amount (Min: ₹100)"
              value={amount}
              onChange={handleManualInput}
              style={styles.input}
              onFocus={(e) => e.target.style.borderColor = "#800020"}
              onBlur={(e) => e.target.style.borderColor = dark ? "#444" : "#d1d5db"}
            />
          </div>
          {error && (
            <p style={styles.error}>
              <span>⚠️</span> {error}
            </p>
          )}
        </div>

        {/* Payment Method Tabs */}
        <div style={styles.paymentTabs}>
          <div
            style={styles.paymentTab(paymentMethod === "bank")}
            onClick={() => setPaymentMethod("bank")}
          >
            <span style={styles.tabIcon}>🏦</span>
            <span style={styles.tabLabel(paymentMethod === "bank")}>Bank Transfer</span>
          </div>
          <div
            style={styles.paymentTab(paymentMethod === "upi")}
            onClick={() => setPaymentMethod("upi")}
          >
            <span style={styles.tabIcon}>📱</span>
            <span style={styles.tabLabel(paymentMethod === "upi")}>UPI</span>
          </div>
          <div
            style={styles.paymentTab(paymentMethod === "online")}
            onClick={() => setPaymentMethod("online")}
          >
            <span style={styles.tabIcon}>💳</span>
            <span style={styles.tabLabel(paymentMethod === "online")}>Online</span>
            <span style={styles.tabBadge}>SOON</span>
          </div>
        </div>

        {/* Payment Method Content */}
        {paymentMethod === "bank" && renderBankTransfer()}
        {paymentMethod === "upi" && renderUPIPayment()}
        {paymentMethod === "online" && renderOnlinePayment()}

        {/* Submit Button */}
        {paymentMethod !== "online" && (
          <div style={{ padding: "0 1.5rem 1.5rem" }}>
            {success ? (
              <div style={styles.successBox}>
                ✅ Request submitted successfully! Funds will be credited after verification.
              </div>
            ) : (
              <button
                style={styles.submitBtn}
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span style={{
                      display: "inline-block",
                      animation: "spin 1s linear infinite"
                    }}>⏳</span>
                    Processing...
                  </>
                ) : (
                  <>
                    <span>💵</span>
                    Submit Deposit Request
                  </>
                )}
              </button>
            )}
          </div>
        )}

      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      {/* Success Modal */}
      <DepositSuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        amount={successAmount}
      />
    </div>
  );
}